import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { INSURANCE_FAQ_CATEGORIES } from "@/content/insurance-corpus/faq-defs";

const ADVICE_EN = [
  "Always read the minimum sums insured from the notification in force on your filing date; the Non-O-A and LTR figures have been revised more than once.",
  "Buying from an insurer licensed in Thailand and recorded in longstay.tgia.org lets officers verify the policy online immediately.",
  "Renew so the cover extends beyond the last day of the stay you request — an early expiry is the most common reason files are returned.",
  "Complete the application form yourself and declare your medical history truthfully so the contract cannot be avoided under section 865.",
  "Keep original receipts, the itemised billing summary and the medical certificate, and ask for English versions on the day of discharge.",
  "Obtain the claim refusal in writing, citing the policy clause relied on, before complaining or litigating.",
  "Diary the two-year limitation period in section 882; do not let the insurer's internal review run it down.",
  "Separate what the condominium juristic person's policy covers from what your own unit policy must cover.",
];

const CAUTIONS_EN = [
  "Relying on old figures for the sum insured and finding the notification in force requires more on the filing day.",
  "Buying foreign cover that does not state clearly that treatment inside Thailand is covered.",
  "Letting an agent complete the application unread, leaving a false statement about medical history on file.",
  "Assuming compulsory motor insurance pays for vehicle repairs when it covers death and bodily injury only.",
  "Driving without a licence valid under Thai law, or over the legal alcohol limit, both standard policy exclusions.",
  "Changing travel dates without obtaining an endorsement amending the cover period.",
  "Treating social security as a full substitute for private cover despite the contracted-hospital and referral rules.",
  "Naming a beneficiary whose name does not match the passport, or omitting the relationship, which complicates the claim.",
];

export const Route = createFileRoute("/en/insurance/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/insurance-corpus");
    return { faqs: m.INSURANCE_FAQ_EN };
  },
  component: EnInsuranceFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Insurance in Thailand for Foreigners — ${faqs.length} FAQs`,
        description:
          "Health cover for Non-O-A, O-X and LTR visas, insurance contract law under sections 865 and 882, claim documents and legalisation, compulsory motor cover, Schengen travel insurance, social security, fire cover and OIC complaints.",
        path: "/en/insurance/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/insurance/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Insurance FAQ", url: `${site}/en/insurance/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/insurance/faq")) },
      ],
    };
  },
});

function EnInsuranceFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Insurance FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Insurance in Thailand for Foreigners: Visa Health Cover, Claims, Motor, Travel, Social Security and Complaints
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers grounded in sections 861–897 of the Civil and Commercial Code, the Life and Non-Life
          Insurance Acts B.E. 2535, the Protection for Motor Vehicle Accident Victims Act B.E. 2535, the published visa
          insurance conditions, the Social Security Office and the OIC.
        </p>

        <section aria-label="Topics" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {INSURANCE_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/insurance/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have our team review your policy and claim pack first</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the visa you are filing, your current insurer and the country that must accept the documents — by
            phone, LINE or email, Monday to Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="insurance" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}